// ---------------------------------------------------------------------------
// Editing a campaign that has left 'draft' (R17b)
//
// Its leads and messages are real, so a sequence or offering change does not
// ride the plain autosave alone: a removed or reordered step goes to
// /sequence/reorder, a step added in this visit to /sequence/steps once its
// debounce settles, a content edit of an existing step to /sequence/steps/:id,
// and the offering to /offering, each of which regenerates (or flags) the
// messages it touches. What goes where, and the bodies and toasts, taken out
// of pages/campaign-wizard.ts unchanged; the page keeps the timers and sends.
// Plain TypeScript.
// ---------------------------------------------------------------------------

import type { OfferingConfig, SequenceStep } from '../types.js';

/** How many times a step submit waits out a "still generating" refusal before giving up. */
export const MAX_STEP_SUBMIT_ATTEMPTS = 6;
/** …and how long it waits between those tries. Six of these covers a slow first batch. */
export const STEP_SUBMIT_RETRY_MS = 2500;
/** The debounce of a step or offering edit: the autosave's. */
export const EDIT_DEBOUNCE_MS = 1500;

/**
 * Content fields that, if changed on an already-confirmed step, require
 * regenerating its messages. Deliberately excludes `channel` — the UI never
 * offers a way to change an existing step's channel (see wiz-sequence.ts), and
 * the server-side endpoint ignores it too.
 */
export const STEP_CONTENT_FIELDS = [
  'delayValue',
  'delayUnit',
  'condition',
  'prompt',
  'subject',
  'subjectPrompt',
  'manualBody',
] as const;

export const stepContentChanged = (a: SequenceStep, b: SequenceStep): boolean =>
  STEP_CONTENT_FIELDS.some(f => a[f] !== b[f]);

/** What one sequence change of a prepared campaign sets off. */
export interface SequenceEditPlan {
  /** Confirmed steps that are gone: their messages are deleted (reorder route). */
  removedConfirmed: string[];
  /** The confirmed steps still there, in their new order. */
  newConfirmedOrder: string[];
  /** The surviving confirmed steps changed their relative order. */
  orderChanged: boolean;
  /** Pending (not yet confirmed) steps the user removed: their debounce is cancelled. */
  cancelPending: string[];
  /**
   * In sequence order, the debounces (re)armed: `create` for a step added in
   * this visit, `update` for a confirmed step whose content changed.
   */
  debounce: { id: string; submit: 'create' | 'update' }[];
}

/**
 * `confirmed`: the ids that already exist server-side (snapshotted at load,
 * grown as new steps get confirmed). Reordering a confirmed step would leave
 * its CampaignMessage.stepIndex stale, so the relative order of SURVIVING
 * confirmed ids is compared — inserting a new step between two confirmed ones,
 * or removing one, is not also a reorder of the ones that did not move.
 */
export function planSequenceEdit(args: {
  previous: SequenceStep[];
  next: SequenceStep[];
  confirmed: ReadonlySet<string>;
  pending: Iterable<string>;
}): SequenceEditPlan {
  const { previous, next, confirmed, pending } = args;
  const previousById = new Map(previous.map(s => [s.id, s]));
  const newIds = new Set(next.map(s => s.id));
  const removedConfirmed = [...confirmed].filter(id => !newIds.has(id));
  const survivingPrevOrder = previous.filter(s => confirmed.has(s.id) && newIds.has(s.id)).map(s => s.id);
  const newConfirmedOrder = next.filter(s => confirmed.has(s.id)).map(s => s.id);
  const orderChanged = survivingPrevOrder.some((id, i) => id !== newConfirmedOrder[i]);
  const cancelPending = [...pending].filter(id => !newIds.has(id));
  const debounce: SequenceEditPlan['debounce'] = [];
  for (const step of next) {
    if (!confirmed.has(step.id)) {
      debounce.push({ id: step.id, submit: 'create' });
      continue;
    }
    // Only when its content actually differs from what's persisted (not on
    // every render, not on a reorder that does not touch its own fields).
    const prev = previousById.get(step.id);
    if (prev && stepContentChanged(prev, step)) debounce.push({ id: step.id, submit: 'update' });
  }
  return { removedConfirmed, newConfirmedOrder, orderChanged, cancelPending, debounce };
}

/** POST /api/campaigns/:id/sequence/steps: our id, not the server's (the autosave persists the sequence with it). */
export const newStepBody = (step: SequenceStep) => ({
  stepId: step.id,
  channel: step.channel,
  delayValue: step.delayValue,
  delayUnit: step.delayUnit,
  condition: step.condition,
  prompt: step.prompt,
  subject: step.subject,
  subjectPrompt: step.subjectPrompt,
  manualBody: step.manualBody,
});

/** PUT /api/campaigns/:id/sequence/steps/:stepId. */
export const stepContentBody = (step: SequenceStep) => ({
  delayValue: step.delayValue,
  delayUnit: step.delayUnit,
  condition: step.condition,
  prompt: step.prompt,
  subject: step.subject,
  subjectPrompt: step.subjectPrompt,
  manualBody: step.manualBody,
});

/** PUT /api/campaigns/:id/offering. */
export const offeringBody = (offering: OfferingConfig) => ({
  name: offering.name,
  description: offering.description,
  talkingPoints: offering.talkingPoints,
});

/** A new step is sent once it has a prompt (an empty one waits for the next edit). */
export const stepReadyToCreate = (step: SequenceStep | undefined): step is SequenceStep => !!step?.prompt?.trim();

/** A content edit is sent for a step still confirmed that has a prompt or a hand-written message. */
export const stepReadyToUpdate = (
  step: SequenceStep | undefined,
  confirmed: ReadonlySet<string>,
): step is SequenceStep => !!step && confirmed.has(step.id) && !!(step.prompt?.trim() || step.manualBody?.trim());

/** The answer of POST …/sequence/steps when it accepted the step. */
export interface StepAdded {
  ineligibleLeads?: number;
  totalLeads?: number;
  missingContactData?: string;
  generableMessages?: number;
}

/**
 * The toast after a step was added. When the new step's channel needs contact
 * data some existing leads lack, they skip it; the way to the full count is
 * named, pause included (the top-up route refuses an ACTIVE campaign: pause →
 * top up → resume is the supported flow).
 */
export function stepAddedToast(data: StepAdded): { kind: 'warning' | 'success'; message: string; ms?: number } {
  if ((data.ineligibleLeads ?? 0) > 0)
    return {
      kind: 'warning',
      message:
        `Step added — ${data.ineligibleLeads} of ${data.totalLeads} leads ${
          data.ineligibleLeads === 1 ? 'is' : 'are'
        } missing ${data.missingContactData} and will skip this step. To reach the full count: pause the ` +
        `campaign, then source new leads via "Add Leads" in the Leads step, then resume.`,
      ms: 10000,
    };
  return {
    kind: 'success',
    message: `Step added — generating ${data.generableMessages} message${data.generableMessages === 1 ? '' : 's'}…`,
  };
}

/** The toast after confirmed steps were removed. */
export const stepsRemovedMessage = (removedMessages: number): string =>
  `Step removed — ${removedMessages} message${removedMessages === 1 ? '' : 's'} deleted.`;
